import { TranslocoService } from '@jsverse/transloco';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { LegendItem, MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries } from '../../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { FilterService } from '../../../../../../_shared/services';
import { MaintenanceDashboardService } from '../../../services/maintenance-dashboard.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { AccountDateFilterRequest } from '../../../../../../_shared/models/accountDateFilter.model';
import { AssetDowntimeDataset, mapAssetDowntimeToChart, roundDowntimeHours } from '../../../helpers/asset-downtime-chart.helper';
import { TimeSeriesGroupingHelper } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { AssetDowntimeTrendModalComponent } from '../../modals/asset-downtime-trend-modal/asset-downtime-trend-modal.component';
import { Subject } from 'rxjs';
import { WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';

@Component({
  selector: 'app-asset-downtime-trend',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, MultiLineChartComponent, EmptyStateComponent],
  templateUrl: './asset-downtime-trend.component.html',
  styleUrl: './asset-downtime-trend.component.scss'
})
export class AssetDowntimeTrendComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private modalService = inject(NgbModal);
  private dashboardService = inject(MaintenanceDashboardService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);

  chartData: MultiLineChartDataPoint[] = [];
  yTicks: number[] = [100, 80, 60, 40, 20, 0];

  series: MultiLineChartSeries[] = [];
  legendItems: LegendItem[] = [];

  title: string = this.transloco.translate('maintenanceDashboard.charts.assetDowntimeTrend');
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter: WidgetFilterData;

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    // Only load data if filter is initialized
    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadDowntimeData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      await this.loadDowntimeData();
    });
  }

  private async loadDowntimeData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds
      };

      const [downtimeResponse, kpiResponse] = await Promise.all([
        this.dashboardService.getAssetDowntimeData(request, this.cancelSubject),
        this.dashboardService.getAssetDowntimeKpi({
          ...request,
          previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
          previousTimeTo: this.widgetFilter.previousCycleEndDate!
        }, this.cancelSubject)
      ]);

      const kpi = kpiResponse.data;
      const { series, legend, datasets } = mapAssetDowntimeToChart(downtimeResponse.data ?? []);

      this.series = series;
      this.legendItems = legend;
      this.chartData = this.processDowntimeData(datasets);
      this.calculateYTicks(this.chartData);
      this.totalValue = kpi?.totalValue ?? 0;
      this.percentageChange = kpi?.percentageChange ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private processDowntimeData(datasets: AssetDowntimeDataset[]): MultiLineChartDataPoint[] {
    if (!datasets.length) return [];

    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    let groupedData;
    switch (groupingType) {
      case 'hourly':
        // Use 6-hour intervals for 24hrs view
        groupedData = TimeSeriesGroupingHelper.groupByHours(datasets);
        break;
      case 'daily':
        // Use day names (Mon, Tue, Wed, etc.) for 1 week view
        groupedData = TimeSeriesGroupingHelper.groupByDays(
          datasets,
          this.widgetFilter.startDate!,
          this.widgetFilter.endDate!
        );
        break;
      default:
        // Use week numbers for 1 month view
        groupedData = TimeSeriesGroupingHelper.groupByWeeks(
          datasets,
          this.widgetFilter.startDate!,
          this.widgetFilter.endDate!
        );
        break;
    }

    return groupedData;
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    if (!data || data.length === 0) {
      this.yTicks = [100, 80, 60, 40, 20, 0];
      return;
    }

    // Find the maximum value across all series
    let maxValue = 0;
    data.forEach(point => {
      this.series.forEach(s => {
        const value = (point[s.key] as number) || 0;
        if (value > maxValue) maxValue = value;
      });
    });

    // Add 20% padding to the max value and round to nearest 20
    const paddedMax = Math.ceil(maxValue * 1.2);
    const roundedMax = Math.max(100, Math.ceil(paddedMax / 20) * 20);

    // Calculate y-axis ticks (6 ticks from max to 0)
    const tickInterval = roundedMax / 5;
    this.yTicks = [];
    for (let i = 5; i >= 0; i--) {
      this.yTicks.push(Math.round(tickInterval * i));
    }
  }

  formatValue(value: number): string {
    return `${roundDowntimeHours(value)} hours`;
  }

  // An arrow function because the chart calls it without `this`
  formatTooltipValue = (hours: number): string =>
    this.transloco.translate('driverManager.duration.hours', { count: roundDowntimeHours(hours) });

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false;
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      // Check all series keys for non-zero values
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(AssetDowntimeTrendModalComponent, modalOptions);
  }
}